import { useState, useEffect, useCallback } from 'react';
import { getAdminPlans, getAdminUserSlotUpgrades } from '../../api/adminApi';
import { fmtIST } from '../../utils/dateFormat';

const cyan = '#00e5ff';

const AdminUserSlotUpgrades = () => {
  const [upgrades, setUpgrades] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [planId, setPlanId] = useState('all');
  const [plans, setPlans] = useState([]);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load plans for filter dropdown
  useEffect(() => {
    getAdminPlans()
      .then((res) => setPlans(res.data.plans || []))
      .catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    getAdminUserSlotUpgrades({
      planId: planId !== 'all' ? planId : undefined,
      search: search || undefined,
      page,
      limit: 20,
    })
      .then((res) => {
        setUpgrades(res.data.upgrades || []);
        setTotal(res.data.total || 0);
        setTotalAmount(res.data.totalAmount || 0);
        setPages(res.data.pages || 1);
      })
      .catch((err) => {
        console.error('Error fetching upgrades:', err);
        setUpgrades([]);
      })
      .finally(() => setLoading(false));
  }, [planId, search, page]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput);
  };

  return (
    <div>
      <h4 style={{ color: '#fff', fontWeight: 700, marginBottom: 4 }}>User Slot Upgrades</h4>
      <div style={{ color: '#555', fontSize: 13, marginBottom: 24 }}>
        {total} total upgrades (primary + renewals) • Total Amount: <span style={{ color: '#66ff99', fontWeight: 600 }}>${totalAmount.toFixed(2)}</span>
      </div>

      {/* Filters & Search */}
      <div style={{ marginBottom: 20 }}>
        <form onSubmit={handleSearch} style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by name, email, mobile, or code..."
            style={{ flex: 1, minWidth: 200, background: 'rgba(0,229,255,0.04)', border: '1px solid rgba(0,229,255,0.2)', borderRadius: 8, padding: '9px 14px', color: '#fff', fontSize: 13, outline: 'none' }}
          />
          <button type="submit" style={{ background: cyan, border: 'none', color: '#000', borderRadius: 8, padding: '9px 20px', fontWeight: 700, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            Search
          </button>
          {search && (
            <button type="button" onClick={() => { setSearchInput(''); setSearch(''); setPage(1); }} style={{ background: 'rgba(255,255,255,0.06)', border: 'none', color: '#888', borderRadius: 8, padding: '9px 14px', fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              Clear
            </button>
          )}
        </form>

        {/* Plan Filter */}
        <div>
          <label style={{ display: 'block', color: '#888', fontSize: 11, fontWeight: 600, marginBottom: 8 }}>Slot / Plan Filter</label>
          <select
            value={planId}
            onChange={(e) => { setPlanId(e.target.value); setPage(1); }}
            style={{
              width: '100%',
              maxWidth: 300,
              background: '#1a1a1a',
              border: '1px solid rgba(0,229,255,0.2)',
              borderRadius: 8,
              padding: '8px 12px',
              color: '#fff',
              fontSize: 13,
              outline: 'none',
              cursor: 'pointer',
            }}
          >
            <option value="all">All Slots</option>
            {plans.map((plan) => (
              <option key={plan._id} value={plan._id}>
                {plan.planName} - ${plan.amount}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Table (scrolls horizontally on mobile) */}
      <div style={{ background: '#111', border: '1px solid rgba(0,229,255,0.1)', borderRadius: 12, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: isMobile ? 900 : 'auto' }}>
            <thead>
              <tr style={{ background: 'rgba(0,229,255,0.07)', borderBottom: '1px solid rgba(0,229,255,0.12)' }}>
                {['#', 'User Name', 'Email', 'Mobile', 'Code', 'Slot', 'Type', 'Amount', 'Upgrade Date', ''].map((h) => (
                  <th key={h} style={{ color: cyan, fontSize: 12, fontWeight: 600, padding: '12px 14px', textAlign: 'left', whiteSpace: 'nowrap' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={9} style={{ color: '#555', textAlign: 'center', padding: 32, fontSize: 13 }}>
                    Loading...
                  </td>
                </tr>
              ) : !upgrades || upgrades.length === 0 ? (
                <tr>
                  <td colSpan={9} style={{ color: '#555', textAlign: 'center', padding: 32, fontSize: 13 }}>
                    No upgrades found
                  </td>
                </tr>
              ) : (
                upgrades.map((u, i) => (
                  <tr
                    key={u._id}
                    style={{
                      borderBottom: '1px solid rgba(255,255,255,0.04)',
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(0,229,255,0.03)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    <td style={{ color: '#555', fontSize: 12, padding: '11px 14px' }}>{(page - 1) * 20 + i + 1}</td>
                    <td style={{ color: '#fff', fontSize: 13, fontWeight: 500, padding: '11px 14px', whiteSpace: 'nowrap' }}>
                      {u.userName}
                    </td>
                    <td style={{ color: '#888', fontSize: 12, padding: '11px 14px' }}>{u.userEmail}</td>
                    <td style={{ color: '#888', fontSize: 12, padding: '11px 14px' }}>{u.userMobile}</td>
                    <td style={{ color: cyan, fontSize: 12, padding: '11px 14px', fontFamily: 'monospace', fontWeight: 600 }}>
                      {u.userCode}
                    </td>
                    <td style={{ color: '#fff', fontSize: 12, fontWeight: 600, padding: '11px 14px' }}>
                      {u.planName}
                    </td>
                    <td style={{ padding: '11px 14px' }}>
                      <span style={{
                        background: u.upgradeType === 'Primary' ? 'rgba(0,229,255,0.1)' : 'rgba(255,170,68,0.1)',
                        border: `1px solid ${u.upgradeType === 'Primary' ? 'rgba(0,229,255,0.25)' : 'rgba(255,170,68,0.25)'}`,
                        color: u.upgradeType === 'Primary' ? '#00e5ff' : '#ffaa44',
                        borderRadius: 4,
                        padding: '2px 8px',
                        fontSize: 11,
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                      }}>
                        {u.upgradeType || 'Primary'}
                      </span>
                    </td>
                    <td style={{ color: '#66ff99', fontSize: 12, fontWeight: 600, padding: '11px 14px' }}>
                      ${u.planAmount}
                    </td>
                    <td style={{ color: '#555', fontSize: 11, padding: '11px 14px', whiteSpace: 'nowrap' }}>
                      {fmtIST(u.activationDate)}
                    </td>
                    <td style={{ padding: '11px 14px', textAlign: 'center' }}>
                      <span style={{ color: '#888', fontSize: 11 }}>—</span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
      </div>

      {/* Pagination */}
      {pages && pages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 20, flexWrap: 'wrap' }}>
          {Array.from({ length: Math.min(pages, 5) }, (_, i) => i + 1).map((p) => (
            <button
              key={p}
              onClick={() => setPage(p)}
              style={{
                background: p === page ? cyan : 'rgba(255,255,255,0.06)',
                border: 'none',
                color: p === page ? '#000' : '#888',
                borderRadius: 6,
                padding: '6px 12px',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {p}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminUserSlotUpgrades;
